/**
 * Schémas Zod — requirements de dossier et verdict de complétude (chunk 9.AC.1).
 *
 * ## Ce que ce contrat garantit
 *
 * Décision PO : « le frontend ne doit jamais reconstruire cette logique ». Tout
 * ce dont l'interface a besoin pour dire au conseiller **où il en est, ce qui
 * manque, qui doit fournir, quelle échéance approche et quelle est la prochaine
 * action** arrive donc calculé du serveur. L'UI traduit, elle ne décide pas.
 *
 * Source DB :
 *   - `app.project_requirements` (0068) — ce que le dossier doit réunir
 *   - `app.missing_information` (0021) — la matérialisation des non satisfaits
 *
 * ## `completeness` ≠ validation d'un formulaire
 *
 * Invariant découvert à l'audit et posé en décision PO : une `form_response`
 * `submitted` est **déjà** complète pour son propre schéma, parce que
 * `buildFormResponseDataSchema(..., { mode: "submit" })` exige les champs requis.
 * On ne déduit donc **jamais** « formulaire soumis = dossier prêt ». Le dossier
 * peut encore manquer un autre formulaire, une pièce, une donnée d'entreprise, un
 * contact ou un élément suivi à la main.
 */

import { z } from "zod";

import { UuidSchema } from "./common.js";

// -----------------------------------------------------------------------------
// Nature d'un requirement
// -----------------------------------------------------------------------------

/**
 * Les cinq natures de l'enum `app.requirement_kind`.
 *
 * Chacune se vérifie par une **observation factuelle** du dossier, jamais par une
 * interprétation. `manual` est la seule qu'aucune observation ne peut satisfaire :
 * elle attend une décision humaine, et c'est explicite plutôt que subi.
 */
export const REQUIREMENT_KINDS = [
  "form_response",
  "company_field",
  "contact_ref",
  "document",
  "manual",
  /**
   * 9.BJ.1 — le mandat d'étude signé par le client (`app.company_mandates`).
   *
   * Une sixième nature plutôt qu'un `document` sous un rôle, parce qu'un mandat
   * porte trois choses qu'une pièce attachée ne sait pas dire : une **validité**
   * (celui de GFS est exclusif trois mois — une pièce présente satisferait le
   * requis pour toujours), une **révocation**, et **deux canaux** — signé chez
   * nous ou déposé signé ailleurs — pour une seule condition.
   *
   * `requirement_key` = un `form_template_versions.id`, comme `form_response` :
   * la version exigée se change en publiant, sans déploiement.
   *
   * Satisfait par l'ENTREPRISE du dossier, pas par le dossier : un mandat valide
   * couvre tous les dossiers de son client.
   */
  "mandate",
  /**
   * 9.BK.1 — la liste d'employés du dossier (`app.censuses`).
   *
   * Une septième nature plutôt qu'un `document` : une pièce attachée est
   * satisfaite dès qu'un fichier existe, alors qu'un census ne l'est que
   * **verrouillé** — c'est-à-dire quand le conseiller a revu ce que le client a
   * déclaré. Le fichier n'est pas la condition ; l'arrêt de l'effectif l'est.
   *
   * Pas un `form_response` non plus : le moteur Formulaires n'a aucune
   * construction répétable, donc N employés × M personnes à charge ne s'exprime
   * pas dans un `schema`.
   *
   * `requirement_key` = **l'UUID du census**, convention `form_response` et
   * `mandate` : la clé désigne la cible à satisfaire, si bien qu'une ligne de
   * requis suffit à savoir de quel census on parle.
   *
   * L'unicité « un dossier, un requis census » ne peut donc plus venir de
   * l'index `(project_id, kind, requirement_key)` — deux census successifs
   * donneraient deux clés. Elle vient d'un index partiel dédié (`0089`), en
   * base, parce qu'une route ajoutée plus tard pourrait oublier une
   * vérification applicative.
   *
   * Le requis est créé **dans la même transaction** que le census : l'oubli
   * d'une exigence rendrait un census non bloquant en silence, et c'est
   * exactement ce que le verrou existe pour empêcher. Une exception passe par
   * le `waive` explicite — motif et audit — jamais par un oubli.
   */
  "census",
] as const;
export type RequirementKind = (typeof REQUIREMENT_KINDS)[number];

/**
 * Qui doit fournir. Même vocabulaire que `missing_information.question_audience`,
 * par construction : l'item matérialisé hérite de l'audience de son requirement,
 * et deux vocabulaires divergents produiraient deux vérités sur le même écran.
 */
export const REQUIREMENT_AUDIENCES = ["client", "broker", "admin", "insurer"] as const;
export type RequirementAudience = (typeof REQUIREMENT_AUDIENCES)[number];

/**
 * Colonnes de `app.companies` qu'un requirement `company_field` peut exiger.
 *
 * **Liste blanche, et c'est délibéré.** Accepter un nom de colonne arbitraire
 * ferait d'un requirement un vecteur d'introspection du schéma : un courtier
 * curieux découvrirait quelles colonnes existent en observant lesquelles sont
 * acceptées. La liste couvre ce qu'un assureur demande réellement pour coter, et
 * s'allonge quand un besoin réel apparaît — pas par anticipation.
 */
export const COMPANY_REQUIREMENT_FIELDS = [
  "legal_name",
  "business_number",
  "industry",
  "address_street",
  "address_city",
  "address_province",
  "address_postal",
  "phone",
  "email",
  "plan_admin_name",
  "plan_admin_email",
  "employee_count",
] as const;
export type CompanyRequirementField = (typeof COMPANY_REQUIREMENT_FIELDS)[number];

/** La seule référence que `contact_ref` sait observer aujourd'hui. */
export const CONTACT_REQUIREMENT_KEY = "project.contact_id" as const;

// -----------------------------------------------------------------------------
// Le requirement sur le wire
// -----------------------------------------------------------------------------

/**
 * Un requirement tel que la route le renvoie.
 *
 * `tenant_id` et les colonnes de créateur sont déclarés **parce que la route les
 * projette** — parité `document-links` et `missing-information`, où la même
 * convention « forensic visible » (9.M.7 D30) rend l'auteur d'une ligne lisible par
 * le cabinet qui la possède.
 *
 * Les déclarer plutôt que les taire est un choix : ma QA a trouvé que le contrat
 * promettait moins que la route ne renvoyait. Ce n'était pas une fuite — un courtier
 * lit son propre `tenant_id`, ses propres `details`, ses propres créateurs — mais une
 * divergence silencieuse rend la revue anti-fuite impossible : on ne peut plus
 * vérifier ce qui part au wire en lisant une seule source.
 */
export const ProjectRequirementSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,
  kind: z.enum(REQUIREMENT_KINDS),
  requirement_key: z.string(),
  label_fr: z.string(),
  label_en: z.string(),
  audience: z.enum(REQUIREMENT_AUDIENCES),
  /**
   * 9.BJ.1 — ce requis interdit-il l'envoi aux assureurs tant qu'il n'est pas
   * satisfait ?
   *
   * Déclaratif, par dossier, plutôt qu'une liste de natures codée dans l'API :
   * un cabinet qui veut bloquer sur autre chose n'a pas à attendre un
   * déploiement, et on lit dans la donnée ce qui bloque.
   */
  blocks_insurer_send: z.boolean(),
  due_at: z.string().nullable(),
  /** Contexte structuré libre, saisi par le courtier. Classé `redact` en rétention. */
  details: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by_user: UuidSchema.nullable(),
  created_by_agent: UuidSchema.nullable(),
});
export type ProjectRequirement = z.infer<typeof ProjectRequirementSchema>;

/**
 * Le requirement tel que le VERDICT l'embarque — volontairement plus étroit.
 *
 * Le verdict est lu à chaque affichage de la page projet, une ligne par exigence.
 * Y répéter `tenant_id`, `details` et les colonnes de créateur multiplierait une
 * surface dont l'interface n'a aucun usage : elle affiche un libellé, une nature,
 * une audience et une échéance. « Exposer moins » n'a de sens que là où rien ne
 * réclame plus, et c'est exactement le cas ici.
 *
 * Le CRUD `/v1/project-requirements` continue de renvoyer la ligne complète, pour
 * qui a besoin de la provenance.
 */
export const RequirementSummarySchema = ProjectRequirementSchema.pick({
  id: true,
  project_id: true,
  kind: true,
  requirement_key: true,
  label_fr: true,
  label_en: true,
  audience: true,
  blocks_insurer_send: true,
  due_at: true,
  created_at: true,
  updated_at: true,
});
export type RequirementSummary = z.infer<typeof RequirementSummarySchema>;

/**
 * Création d'un requirement.
 *
 * `requirement_key` est validé **par nature** côté serveur, pas ici : un
 * raffinement Zod cross-champ produirait un message d'erreur générique là où le
 * conseiller a besoin de savoir *laquelle* des cinq formes attendues il a ratée.
 * Le serveur renvoie un code du registre bilingue, avec la valeur fautive.
 */
export const CreateProjectRequirementSchema = z.object({
  project_id: UuidSchema,
  kind: z.enum(REQUIREMENT_KINDS),
  requirement_key: z.string().trim().min(1).max(200),
  label_fr: z.string().trim().min(1).max(200),
  label_en: z.string().trim().min(1).max(200),
  audience: z.enum(REQUIREMENT_AUDIENCES).optional(),
  /**
   * Omis, la valeur dépend de la nature : un requis `mandate` bloque, les autres
   * non. Une valeur par défaut en base ne peut pas exprimer ça, et faire bloquer
   * tout requis rendrait l'envoi impossible dès qu'un conseiller note une tâche.
   */
  blocks_insurer_send: z.boolean().optional(),
  due_at: z.string().datetime({ offset: true }).nullable().optional(),
  details: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type CreateProjectRequirementInput = z.infer<typeof CreateProjectRequirementSchema>;

export const ProjectRequirementFilterSchema = z.object({
  project_id: UuidSchema,
});

// -----------------------------------------------------------------------------
// Verdict du dossier
// -----------------------------------------------------------------------------

/**
 * État du dossier. Trois valeurs, pas un pourcentage.
 *
 * Un « 73 % complet » invite à négocier avec soi-même ; « il manque le registre
 * des employés » se traite. Et la distinction entre les deux états non prêts est
 * la question que le conseiller pose vraiment : **est-ce que j'attends quelqu'un,
 * ou est-ce que c'est à moi de jouer ?**
 */
export const COMPLETENESS_STATUSES = [
  /** Aucun requirement non satisfait — le dossier peut partir aux assureurs. */
  "ready",
  /** Il manque des choses, et au moins une m'incombe. */
  "action_required",
  /** Il manque des choses, mais tout est chez quelqu'un d'autre. */
  "waiting_on_others",
] as const;
export type CompletenessStatus = (typeof COMPLETENESS_STATUSES)[number];

/**
 * La prochaine action, décidée côté serveur.
 *
 * Volontairement un code fermé et non une phrase : l'interface le traduit dans la
 * langue du conseiller et y accroche le bon bouton. Une phrase renvoyée par
 * l'API serait ni traduisible ni cliquable.
 */
export const NEXT_ACTIONS = [
  /** Rien ne manque — proposer la préparation des demandes assureurs. */
  "prepare_quote_requests",
  /** Des éléments incombent au conseiller — les lui montrer. */
  "complete_broker_items",
  /** Tout est chez le client — relancer, ou attendre. */
  "follow_up_with_client",
  /** Tout est chez un tiers (admin, assureur) — attendre. */
  "await_third_party",
  /** Aucun requirement déclaré : le dossier est prêt, mais rien n'a été exigé. */
  "define_requirements",
] as const;
export type NextAction = (typeof NEXT_ACTIONS)[number];

/**
 * Un requis, avec son état observé et l'item de travail qui le porte.
 *
 * `missing_information_id` est non-null exactement quand le requis n'est pas
 * satisfait ET qu'un item vivant existe — c'est lui qui porte les actions
 * `fulfill` / `waive` / `reopen` / `supersede` déjà livrées en 9.I.
 */
export const RequirementStateSchema = z.object({
  requirement: RequirementSummarySchema,
  satisfied: z.boolean(),
  /**
   * Pourquoi c'est satisfait, ou pourquoi non. Code fermé, traduit par l'UI.
   * `waived` compte comme satisfait : le conseiller a décidé, on ne le lui
   * redemande pas à chaque rechargement.
   */
  reason: z.enum(["observed", "waived", "not_observed", "awaiting_human_decision"]),
  missing_information_id: UuidSchema.nullable(),
  missing_information_status: z
    .enum(["open", "in_progress", "fulfilled", "waived", "superseded"])
    .nullable(),
  overdue: z.boolean(),
  due_soon: z.boolean(),
});
export type RequirementState = z.infer<typeof RequirementStateSchema>;

/**
 * Le verdict complet du dossier, tel que la page projet le consomme.
 *
 * Tout est calculé serveur. L'UI n'additionne rien, ne compare aucune date, ne
 * décide d'aucune prochaine action.
 */
export const ProjectCompletenessSchema = z.object({
  project_id: UuidSchema,
  completeness_status: z.enum(COMPLETENESS_STATUSES),
  requirements_total: z.number().int().nonnegative(),
  requirements_satisfied: z.number().int().nonnegative(),
  requirements_missing: z.number().int().nonnegative(),
  /**
   * Audiences qui portent au moins un requis non satisfait, dédupliquées et
   * ordonnées comme `REQUIREMENT_AUDIENCES`. Vide quand le dossier est prêt.
   * C'est la réponse à « qui attend après qui ».
   */
  waiting_on: z.array(z.enum(REQUIREMENT_AUDIENCES)),
  next_action: z.enum(NEXT_ACTIONS),
  overdue_count: z.number().int().nonnegative(),
  due_soon_count: z.number().int().nonnegative(),
  /** Détail requis par requis, dans l'ordre : non satisfaits d'abord. */
  requirements: z.array(RequirementStateSchema),
});
export type ProjectCompleteness = z.infer<typeof ProjectCompletenessSchema>;

/**
 * Fenêtre « échéance proche », en jours.
 *
 * Sept jours : un requis dû dans six jours mérite d'être vu aujourd'hui, un requis
 * dû dans trois semaines encombrerait l'écran. La valeur est ici et pas dans un
 * `env` parce qu'aucun tenant ne l'a encore demandée différente — l'introduire en
 * paramètre avant qu'un besoin réel existe coûterait une variable d'environnement
 * de plus à documenter pour personne.
 */
export const DUE_SOON_WINDOW_DAYS = 7;

// -----------------------------------------------------------------------------
// Création d'un manquant à la main
// -----------------------------------------------------------------------------

/**
 * `POST /v1/missing-information` — l'ajout manuel du conseiller.
 *
 * Le serveur crée en UNE transaction un requirement `kind=manual` **et** son
 * item. Il n'existe donc qu'un seul concept capable d'influencer le verdict, au
 * lieu de deux classes d'items aux comportements différents — un item rattaché à
 * un requirement et un item orphelin qui ne serait jamais réconcilié.
 */
export const CreateMissingInformationSchema = z.object({
  project_id: UuidSchema,
  label_fr: z.string().trim().min(1).max(200),
  label_en: z.string().trim().min(1).max(200),
  audience: z.enum(REQUIREMENT_AUDIENCES).optional(),
  due_at: z.string().datetime({ offset: true }).nullable().optional(),
  details: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type CreateMissingInformationInput = z.infer<typeof CreateMissingInformationSchema>;
